import { useEffect, useState, Fragment } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { fmtDateTime } from "../lib/format";
import { Heartbeat, ArrowsClockwise, CheckCircle, WarningCircle, CaretDown, CaretRight, Receipt, Warning, PencilSimple, Check, X, DownloadSimple, ArrowsLeftRight, Wrench } from "@phosphor-icons/react";

const SEV = {
  high: { bg: "#FEE2E2", fg: "#991B1B", label: "HIGH" },
  medium: { bg: "#FEF3C7", fg: "#92400E", label: "MEDIUM" },
  low: { bg: "#E0E7FF", fg: "#3730A3", label: "LOW" },
};

const TP_ISSUE = {
  endpcode_missing: { bg: "#FEE2E2", fg: "#991B1B", label: "EndPcode missing" },
  no_voucher_link: { bg: "#FEF3C7", fg: "#92400E", label: "No voucher link" },
  endpcode_blank: { bg: "#E0E7FF", fg: "#3730A3", label: "EndPcode blank" },
};

const COLS = {
  dup_invoices: ["comp_code", "inv_no", "customer_name", "copies", "sources", "sale_dates"],
  overshipped_pos: ["ms_custpo", "ms_pono", "customer_name", "ordered", "shipped", "excess"],
  dispatched_no_invoice: ["voc_no", "customer_name", "inv_no", "type", "dispatch_date", "qty"],
  export_billed_inr: ["customer_name", "pcode", "inv_no", "currency_code", "customer_currency", "exch_rate", "sale_date"],
  invoices_no_po: ["customer_name", "inv_no", "sale_date", "lines"],
};

export default function DataHealth() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const [audit, setAudit] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState({});
  const [fix, setFix] = useState(null); // {docId, inv, comp, saleNoc, reason, busy}

  // ── Transfer-Pricing EndPcode audit (for the DBA) ──────────────────────────
  const _today = new Date().toISOString().slice(0, 10);
  const [tp, setTp] = useState(null);
  const [tpBusy, setTpBusy] = useState(false);
  const [tpOpen, setTpOpen] = useState(true);
  const [tpSince, setTpSince] = useState("2025-01-01");
  const [tpUntil, setTpUntil] = useState(_today);
  const [tpDown, setTpDown] = useState(false);

  const loadTp = () => {
    setTpBusy(true);
    api.get(`/admin/audit/tp-endpcode`, { params: { since: tpSince, until: tpUntil } })
      .then((r) => setTp(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load TP EndPcode audit"))
      .finally(() => setTpBusy(false));
  };
  const downloadTpCsv = () => {
    setTpDown(true);
    api.get(`/admin/audit/tp-endpcode`, { params: { since: tpSince, until: tpUntil, format: "csv" }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `tp_endpcode_audit_${tpSince}_${tpUntil}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("CSV download failed"))
      .finally(() => setTpDown(false));
  };

  // ── Duplicate-affected items audit (over-counting duplicate invoices) ──────
  const [dupDown, setDupDown] = useState(false);
  const downloadDupItems = () => {
    setDupDown(true);
    api.get(`/admin/audit/dup-affected-items`, { params: { format: "csv" }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `dup_affected_items_${_today}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        toast.success("Duplicate-affected items audit downloaded");
      })
      .catch(() => toast.error("CSV download failed"))
      .finally(() => setDupDown(false));
  };

  // ── Over-shipped POs — full CSV (the on-screen check caps at 100 rows) ─────
  const [osDown, setOsDown] = useState(false);
  const downloadOvershipped = () => {
    setOsDown(true);
    api.get(`/admin/audit/overshipped-pos`, { params: { format: "csv" }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `overshipped_pos_${_today}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        toast.success("Over-shipped POs audit downloaded");
      })
      .catch(() => toast.error("CSV download failed"))
      .finally(() => setOsDown(false));
  };

  // ── Over-shipped POs — on-screen expandable drilldown (ERP-verified) ──────
  const [osRows, setOsRows] = useState(null);
  const [osListOpen, setOsListOpen] = useState(false);
  const [osLoading, setOsLoading] = useState(false);
  const [osExpand, setOsExpand] = useState({}); // pono -> detail | "loading" | "error"
  const toggleOsList = () => {
    const open = !osListOpen;
    setOsListOpen(open);
    if (open && osRows === null) {
      setOsLoading(true);
      api.get(`/admin/audit/overshipped-pos`)
        .then((r) => setOsRows(r.data.rows || []))
        .catch(() => toast.error("Failed to load over-shipped POs"))
        .finally(() => setOsLoading(false));
    }
  };
  const loadOsDetail = (pono) =>
    api.get(`/admin/audit/overship-detail`, { params: { pono } })
      .then((r) => setOsExpand((p) => ({ ...p, [pono]: r.data })))
      .catch(() => setOsExpand((p) => ({ ...p, [pono]: "error" })));
  const toggleOsDetail = (pono) => {
    setOsExpand((prev) => {
      if (prev[pono]) { const n = { ...prev }; delete n[pono]; return n; }
      return { ...prev, [pono]: "loading" };
    });
    if (!osExpand[pono]) loadOsDetail(pono);
  };
  const removePhantom = (pono, inv) => {
    if (!window.confirm(`Remove phantom invoice ${inv.ms_inv_no} (absent from the live ERP, qty ${inv.qty})?\nA backup is kept and it is only removed after re-verifying it is not in the ERP.`)) return;
    api.post(`/admin/onetime/purge-one-phantom-invoice`, { id: inv.id })
      .then((r) => { toast.success(`Removed phantom invoice ${r.data.ms_inv_no}`); return loadOsDetail(pono); })
      .then(() => { setOsRows(null); if (osListOpen) api.get(`/admin/audit/overshipped-pos`).then((r) => setOsRows(r.data.rows || [])); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to remove"));
  };


  // ── Legrand ERP PO audit (raw po_dispatch CustPO vs CustPO2) — production only ──
  const [lgDown, setLgDown] = useState(false);
  const downloadLegrandAudit = async () => {
    setLgDown(true);
    try {
      const r = await api.get(`/dispatch/legrand-po-audit.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url; a.download = `legrand_po_audit_${_today}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      const flagged = r.headers?.["x-audit-flagged"];
      const total = r.headers?.["x-audit-rows"];
      toast.success(`Legrand PO audit downloaded — ${total || 0} lines, ${flagged || 0} flagged`);
    } catch (e) {
      // The raw ERP dispatch tables live in the portal DB (production only).
      let msg = "Download failed";
      try { msg = JSON.parse(await e.response?.data?.text?.())?.detail || msg; } catch { /* blob */ }
      toast.error(e.response?.status === 503 ? "Portal DB not reachable — run this on production." : msg);
    } finally {
      setLgDown(false);
    }
  };

  // ── Superseded-voucher audit (vouchers still carrying an old invoice# → DBA voids it) ──
  const [svRows, setSvRows] = useState(null);
  const [svOpen, setSvOpen] = useState(false);
  const [svLoading, setSvLoading] = useState(false);
  const [svDown, setSvDown] = useState(false);
  const toggleSv = () => {
    const next = !svOpen;
    setSvOpen(next);
    if (next && svRows === null) {
      setSvLoading(true);
      api.get(`/admin/audit/superseded-voucher-invoices`)
        .then((r) => setSvRows(r.data))
        .catch((e) => toast.error(e.response?.data?.detail || "Failed to load superseded-voucher audit"))
        .finally(() => setSvLoading(false));
    }
  };
  const downloadSv = () => {
    setSvDown(true);
    api.get(`/admin/audit/superseded-voucher-invoices`, { params: { format: "csv" }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `superseded_vouchers_${_today}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        toast.success("Superseded-voucher audit downloaded");
      })
      .catch(() => toast.error("Download failed"))
      .finally(() => setSvDown(false));
  };

  // ── Bad-LineUID watchdog (gsti rows whose LineUID is the ERP placeholder 'LineUId') ──
  const [luRows, setLuRows] = useState(null);
  const [luOpen, setLuOpen] = useState(false);
  const [luLoading, setLuLoading] = useState(false);
  const [luDown, setLuDown] = useState(false);
  const toggleLu = () => {
    const next = !luOpen;
    setLuOpen(next);
    if (next && luRows === null) {
      setLuLoading(true);
      api.get(`/admin/audit/bad-lineuid`)
        .then((r) => setLuRows(r.data))
        .catch((e) => toast.error(e.response?.data?.detail || "Failed to load bad-LineUID audit"))
        .finally(() => setLuLoading(false));
    }
  };
  const downloadLu = () => {
    setLuDown(true);
    api.get(`/admin/audit/bad-lineuid`, { params: { format: "csv" }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url; a.download = `bad_lineuid_${_today}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        toast.success("Bad-LineUID audit downloaded");
      })
      .catch(() => toast.error("Download failed"))
      .finally(() => setLuDown(false));
  };

  const load = (refresh = false) => {
    setBusy(true);
    api.get(`/ms/data-health${refresh ? "?refresh=true" : ""}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load data health"))
      .finally(() => setBusy(false));
  };
  const loadAudit = () => {
    setAuditBusy(true);
    api.get(`/admin/audit/duplicate-invoice-numbers`)
      .then((r) => setAudit(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load invoice-number audit"))
      .finally(() => setAuditBusy(false));
  };
  useEffect(() => { load(); loadAudit(); loadTp(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startFix = (docId, comp, inv) =>
    setFix({ docId, inv: String(inv ?? ""), comp: comp || "", saleNoc: "", reason: "", busy: false });

  const submitFix = () => {
    if (!fix?.inv?.trim()) { toast.error("Enter the correct invoice number"); return; }
    setFix((f) => ({ ...f, busy: true }));
    api.patch(`/admin/audit/fix-invoice-number`, {
      doc_id: fix.docId,
      new_inv_no: fix.inv.trim(),
      new_comp_code: fix.comp?.trim() || null,
      new_sale_noc: fix.saleNoc?.trim() || null,
      reason: fix.reason?.trim() || null,
    })
      .then(() => { toast.success("Invoice corrected against ERP truth"); setFix(null); loadAudit(); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Fix failed"); setFix((f) => ({ ...f, busy: false })); });
  };

  const fmt = (v) => Array.isArray(v) ? v.join(", ") : (v === null || v === undefined || v === "" ? "—" : String(v));

  // ── One-time corrections (super-admin · dry-run → confirm → apply · auto-backup) ──
  const [corr, setCorr] = useState({ pcode: "", fromInv: "", toInv: "", item: "" });
  const [mis, setMis] = useState({ inv: "", item: "", po: "" });
  const [dupPcode, setDupPcode] = useState("");
  const [dupDiag, setDupDiag] = useState(null); // persistent diagnostic text from last dry-run
  const [corrBusy, setCorrBusy] = useState("");
  const runCorr = async (ep, params, label) => {
    setCorrBusy(label);
    try {
      const dry = (await api.post(ep, null, { params: { ...params, apply: false } })).data;
      const previewBody = JSON.stringify(
        dry.detail || dry.changes || dry.by_customer || dry.rollup || dry, null, 1
      ).slice(0, 900);
      // Surface any backend diagnostic/summary message (e.g. the seed-dup "[scanned … ]" line)
      const preview = dry.message ? `${dry.message}\n\n${previewBody}` : previewBody;
      if (!window.confirm(`DRY RUN — ${label}\n\n${preview}\n\nApply these changes now? (a backup is taken first)`)) {
        setCorrBusy(""); return;
      }
      const res = (await api.post(ep, null, { params: { ...params, apply: true } })).data;
      if (res.applied === false) {
        toast.info(res.message || `${label}: nothing changed`);
        loadAudit(); return;
      }
      toast.success(`${label} applied — ${res.dropped_spurious_lines ?? res.merged_into_existing ?? res.renamed ?? res.seeds_updated ?? res.lines_removed ?? res.purged ?? res.deleted ?? res.removed ?? res.archived ?? "done"}`);
      loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally { setCorrBusy(""); }
  };
  // Dedicated seed-dup cleanup: always captures the backend diagnostic into a persistent box
  // (so the split-brain Prod-vs-Preview investigation can read the exact scan counts).
  const runDupClean = async () => {
    const label = "Remove shipped-PO seed duplicates";
    const params = dupPcode.trim() ? { pcode: dupPcode.trim() } : {};
    setCorrBusy(label);
    setDupDiag(null);
    try {
      const dry = (await api.post("/admin/onetime/remove-shipped-seed-dup-pos", null,
        { params: { ...params, apply: false } })).data;
      const diagText = dry.message
        || (dry.diagnostic ? JSON.stringify(dry.diagnostic) : "")
        || "(no diagnostic returned — Production may be running an older build; redeploy required)";
      setDupDiag({ text: diagText, diag: dry.diagnostic || null, count: dry.seed_docs_to_remove || 0,
        samples: dry.erp_closed_samples || [], phantoms: dry.phantom_samples || [] });
      const n = dry.seed_docs_to_remove || 0;
      if (!n) { toast.info(`No seed duplicates found (see diagnostic below)`); return; }
      const preview = JSON.stringify(dry.detail || [], null, 1).slice(0, 900);
      if (!window.confirm(`DRY RUN — ${label}\n\n${dry.message || ""}\n\n${n} seed doc(s) to remove:\n${preview}\n\nApply now? (backup taken first)`)) return;
      const res = (await api.post("/admin/onetime/remove-shipped-seed-dup-pos", null,
        { params: { ...params, apply: true } })).data;
      toast.success(`${label} applied — removed ${res.removed ?? "?"} (backup ${res.backup_batch || "kept"})`);
      loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${label} failed`);
    } finally { setCorrBusy(""); }
  };
  const runErpSync = async () => {
    setCorrBusy("erpsync");
    try {
      await api.post(`/replica/erp-invoice-sync/run-auto-now`, {});
      toast.success("ERP invoice sync triggered — new twins blocked & existing ones pruned");
      setTimeout(loadAudit, 5000);
    } catch (e) { toast.error(e.response?.data?.detail || "ERP sync failed"); }
    finally { setCorrBusy(""); }
  };
  const [repDown, setRepDown] = useState(false);
  const downloadSeedReport = () => {
    setRepDown(true);
    api.get(`/admin/audit/seed-missing-lines`, { params: { since: "2025-12-01", format: "xlsx" }, responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
        const a = document.createElement("a");
        a.href = url; a.download = `seed_missing_lines_${_today}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        toast.success("Seed-missing-lines report downloaded");
      })
      .catch(() => toast.error("Report download failed"))
      .finally(() => setRepDown(false));
  };

  return (
    <Layout breadcrumb={["Data Health"]}>
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Heartbeat size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>DATA HEALTH — ERP SOURCE-DATA CHECKS</span>
        </span>
        <button className="jde-btn jde-btn-sm" onClick={() => load(true)} disabled={busy} data-testid="dh-refresh">
          <ArrowsClockwise size={11} weight="bold" /> {busy ? "Scanning…" : "Re-scan"}
        </button>
      </div>

      {data && (
        <div className="jde-panel" style={{ marginTop: 12, padding: 16, display: "flex", alignItems: "center", gap: 12 }} data-testid="dh-summary">
          {data.healthy
            ? <CheckCircle size={28} weight="fill" color="#16A34A" />
            : <WarningCircle size={28} weight="fill" color="#DC2626" />}
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {data.healthy ? "All clear — no anomalies detected" : `${data.total_issues} issue${data.total_issues === 1 ? "" : "s"} across ${data.checks.length} checks`}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>
              Last scanned {fmtDateTime(data.generated_at)}
            </div>
          </div>
        </div>
      )}

      {/* ── One-time Corrections (super-admin · dry-run → confirm → apply · auto-backup) ── */}
      {isSuper && (
        <div className="jde-panel" style={{ marginTop: 20 }} data-testid="dh-corrections-panel">
          <div className="jde-panel-header">
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Wrench size={14} weight="bold" />
              <strong>CORRECTIONS — ONE-TIME DATA FIXES</strong>
            </span>
            <span className="jde-badge" style={{ background: SEV.medium.bg, color: SEV.medium.fg, fontWeight: 700, fontSize: 9 }}>
              SUPER ADMIN
            </span>
          </div>
          <div className="jde-panel-body">
            <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
              Trigger the one-time cleanup endpoints from here (no more <span className="text-mono">curl</span>). Every action
              runs a <strong>dry-run first</strong>, shows a preview to confirm, then applies with an automatic backup to
              <span className="text-mono"> invoice_cleanup_backup</span>.
            </p>

            {/* Correct invoice-number typo (needs pcode + from/to + optional item) */}
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <PencilSimple size={13} weight="bold" /> Correct invoice-number typo
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  Customer (pcode)
                  <input className="jde-input" style={{ marginTop: 4, width: 110 }} value={corr.pcode}
                    onChange={(e) => setCorr({ ...corr, pcode: e.target.value.toUpperCase() })}
                    data-testid="corr-pcode-input" placeholder="e.g. GTEK" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  From inv #
                  <input className="jde-input" style={{ marginTop: 4, width: 110 }} value={corr.fromInv}
                    onChange={(e) => setCorr({ ...corr, fromInv: e.target.value })}
                    data-testid="corr-from-inv-input" placeholder="50555" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  To inv #
                  <input className="jde-input" style={{ marginTop: 4, width: 110 }} value={corr.toInv}
                    onChange={(e) => setCorr({ ...corr, toInv: e.target.value })}
                    data-testid="corr-to-inv-input" placeholder="50556" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  Item (opt · targets one)
                  <input className="jde-input" style={{ marginTop: 4, width: 150 }} value={corr.item}
                    onChange={(e) => setCorr({ ...corr, item: e.target.value })}
                    data-testid="corr-item-input" placeholder="DM-0033" />
                </label>
                <button className="jde-btn jde-btn-sm jde-btn-primary"
                  disabled={!!corrBusy || !corr.pcode.trim() || !corr.fromInv.trim() || !corr.toInv.trim()}
                  onClick={() => runCorr("/admin/onetime/correct-invoice-number", {
                    pcode: corr.pcode.trim(), from_inv: corr.fromInv.trim(), to_inv: corr.toInv.trim(),
                    ...(corr.item.trim() ? { contains_item: corr.item.trim() } : {}),
                  }, "Correct invoice typo")}
                  data-testid="corr-fix-invoice-btn">
                  <Check size={11} weight="bold" /> {corrBusy === "Correct invoice typo" ? "Running…" : "Correct typo"}
                </button>
              </div>
            </div>

            {/* Remove shipped-PO seed duplicates (Excel copy of an already-shipped ERP PO) */}
            <div style={{ border: "1px solid #BFDBFE", background: "#EFF6FF", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <Wrench size={13} weight="bold" /> Remove shipped-PO seed duplicates
              </div>
              <p className="muted" style={{ fontSize: 10, margin: "0 0 8px" }}>
                Removes stale Excel-seed PO copies that duplicate an ERP PO already fully shipped/closed
                (the same order counted twice, so a shipped PO wrongly shows OPEN/OVERDUE). The accurate
                shipped ERP record is kept — the PO then reads as shipped. Applied ONLY when the ERP twin
                is fully closed and matches every seed line by item + ordered qty. Backed up to
                <span className="text-mono"> purchase_orders_dup_backup</span> (reversible). Leave the box
                blank to scan <strong>all customers</strong>.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  Customer (pcode · optional)
                  <input className="jde-input" style={{ marginTop: 4, width: 150 }} value={dupPcode}
                    onChange={(e) => setDupPcode(e.target.value.toUpperCase())}
                    data-testid="dup-pcode-input" placeholder="blank = all customers" />
                </label>
                <button className="jde-btn jde-btn-sm jde-btn-primary"
                  disabled={!!corrBusy}
                  onClick={runDupClean}
                  data-testid="dup-remove-btn">
                  <Check size={11} weight="bold" /> {corrBusy === "Remove shipped-PO seed duplicates" ? "Running…" : "Preview & remove"}
                </button>
              </div>
              {dupDiag && (
                <div data-testid="dup-diagnostic-box"
                  style={{ marginTop: 10, padding: 10, borderRadius: 6, background: "#FFFFFF",
                    border: "1px solid #93C5FD", fontSize: 11, color: "#1E3A8A" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Diagnostic (last dry-run)</div>
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontFamily: "Inter, sans-serif", fontSize: 11 }}>{dupDiag.text}</pre>
                  {Array.isArray(dupDiag.samples) && dupDiag.samples.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontWeight: 700, marginBottom: 3, fontSize: 10, color: "#475569" }}>Sample closed/shipped ERP docs</div>
                      <div style={{ maxHeight: 180, overflow: "auto" }}>
                        <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse", fontFamily: "Inter, sans-serif" }}>
                          <thead><tr style={{ textAlign: "left", color: "#64748B" }}>
                            <th style={{ padding: "2px 6px" }}>pcode</th><th style={{ padding: "2px 6px" }}>custpo</th>
                            <th style={{ padding: "2px 6px" }}>norm</th><th style={{ padding: "2px 6px" }}>pono</th>
                            <th style={{ padding: "2px 6px" }}>status</th><th style={{ padding: "2px 6px" }}>match</th>
                          </tr></thead>
                          <tbody>
                            {dupDiag.samples.map((s, i) => (
                              <tr key={i} style={{ borderTop: "1px solid #E2E8F0" }}>
                                <td style={{ padding: "2px 6px" }}>{String(s.pcode)}</td>
                                <td style={{ padding: "2px 6px" }}>{String(s.raw_custpo)}</td>
                                <td style={{ padding: "2px 6px" }}>{String(s.norm_custpo)}</td>
                                <td style={{ padding: "2px 6px" }}>{String(s.pono)}</td>
                                <td style={{ padding: "2px 6px" }}>{String(s.status)}</td>
                                <td style={{ padding: "2px 6px", color: s.match === "seed_same_grp" ? "#166534" : s.match?.startsWith("seed_under") ? "#B45309" : "#64748B" }}>{String(s.match)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {Array.isArray(dupDiag.phantoms) && dupDiag.phantoms.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 700, marginBottom: 3, fontSize: 10, color: "#B45309" }}>Phantom groups (shipped-closed copy + open-nothing-shipped copies) — raw markers</div>
                      {dupDiag.phantoms.map((g, gi) => (
                        <div key={gi} style={{ marginBottom: 8, padding: 6, border: "1px solid #FED7AA", borderRadius: 5, background: "#FFFBEB" }}>
                          <div style={{ fontWeight: 700, fontFamily: "Inter, sans-serif", fontSize: 10 }}>{g.group}</div>
                          {[...(g.closed || []).map(m => ({ ...m, _kind: "CLOSED" })), ...(g.open_phantom || []).map(m => ({ ...m, _kind: "OPEN" }))].map((m, mi) => (
                            <div key={mi} style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: m._kind === "CLOSED" ? "#166534" : "#9A3412" }}>
                              {m._kind} · pono={String(m.pono)} · src={String(m.source)} · is_seed={String(m.is_seed)} · line_uid={String(m.has_line_uid)} · ord={m.ordered} · ship={m.shipped} · pend={m.pending} · {String(m.status)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Prune a MISFILED seed line (a duplicate of another invoice's line) */}
            <div style={{ border: "1px solid #FDE68A", background: "#FFFBEB", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                <Warning size={13} weight="bold" /> Prune misfiled seed line
              </div>
              <p className="muted" style={{ fontSize: 10, margin: "0 0 8px" }}>
                Removes a line the Excel import placed on the WRONG invoice (a duplicate of another
                invoice's real line), which makes a PO show the same shipment twice. Applied ONLY when
                the live ERP confirms the line is absent from its own invoice AND present on a different
                one. Uses the <span className="text-mono">Customer (pcode)</span> from the box above.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  Seed invoice #
                  <input className="jde-input" style={{ marginTop: 4, width: 120 }} value={mis.inv}
                    onChange={(e) => setMis({ ...mis, inv: e.target.value })}
                    data-testid="corr-mis-inv-input" placeholder="2900009" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  Item code
                  <input className="jde-input" style={{ marginTop: 4, width: 130 }} value={mis.item}
                    onChange={(e) => setMis({ ...mis, item: e.target.value })}
                    data-testid="corr-mis-item-input" placeholder="5989829" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                  Misfiled PO #
                  <input className="jde-input" style={{ marginTop: 4, width: 140 }} value={mis.po}
                    onChange={(e) => setMis({ ...mis, po: e.target.value })}
                    data-testid="corr-mis-po-input" placeholder="408874537" />
                </label>
                <button className="jde-btn jde-btn-sm"
                  disabled={!!corrBusy || !corr.pcode.trim() || !mis.inv.trim() || !mis.item.trim() || !mis.po.trim()}
                  onClick={() => runCorr("/admin/onetime/prune-misfiled-seed-line", {
                    pcode: corr.pcode.trim(), inv_no: mis.inv.trim(),
                    item: mis.item.trim(), po: mis.po.trim(),
                  }, "Prune misfiled seed line")}
                  data-testid="corr-prune-misfiled-btn">
                  <Warning size={11} weight="bold" /> {corrBusy === "Prune misfiled seed line" ? "Pruning…" : "Prune misfiled line"}
                </button>
              </div>
            </div>

            {/* Merge same-buyer duplicate customers (e.g. Amphenol AMP-AAIP + AMPAAIP) */}
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Merge duplicate customers</div>
              <p className="muted" style={{ fontSize: 10, margin: "0 0 8px" }}>
                Folds the same buyer booked under two customer codes/spellings into ONE canonical
                identity across all POs, and archives the duplicate customer record. (Registered:
                Amphenol Andrew Antennas → <span className="text-mono">AMP-AAIP</span>.) Run once per environment.
              </p>
              <button className="jde-btn jde-btn-sm"
                disabled={!!corrBusy}
                onClick={() => runCorr("/admin/onetime/normalize-customer", {}, "Merge duplicate customers")}
                data-testid="corr-merge-customers-btn">
                <ArrowsClockwise size={11} weight="bold" /> {corrBusy === "Merge duplicate customers" ? "Merging…" : "Merge duplicate customers"}
              </button>
            </div>

            {/* Per-customer buttons: clean dupes + backfill missing lines */}
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Per-customer maintenance (uses the pcode above)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <button className="jde-btn jde-btn-sm"
                  disabled={!!corrBusy || !corr.pcode.trim()}
                  onClick={() => runCorr("/admin/onetime/cleanup-domestic-none-dupes", { pcode: corr.pcode.trim() }, "Clean domestic dupes")}
                  data-testid="corr-clean-dupes-btn">
                  <Warning size={11} weight="bold" /> {corrBusy === "Clean domestic dupes" ? "Running…" : "Clean domestic dupes"}
                </button>
                <button className="jde-btn jde-btn-sm"
                  disabled={!!corrBusy || !corr.pcode.trim()}
                  onClick={() => runCorr("/admin/onetime/backfill-seed-missing-lines", { pcode: corr.pcode.trim() }, "Backfill missing seed lines")}
                  data-testid="corr-backfill-btn">
                  <ArrowsClockwise size={11} weight="bold" /> {corrBusy === "Backfill missing seed lines" ? "Running…" : "Backfill missing lines"}
                </button>
              </div>
              <p className="muted" style={{ fontSize: 10, margin: "8px 0 0" }}>
                Enter a customer pcode above (e.g. <span className="text-mono">BIPL</span>, <span className="text-mono">LGS</span>) before running these.
              </p>
            </div>

            {/* Global actions: run ERP sync + download seed report */}
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8 }}>Global</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                <button className="jde-btn jde-btn-sm"
                  disabled={!!corrBusy}
                  onClick={runErpSync}
                  data-testid="corr-erp-sync-btn">
                  <ArrowsClockwise size={11} weight="bold" /> {corrBusy === "erpsync" ? "Syncing…" : "Run ERP invoice sync"}
                </button>
                <button className="jde-btn jde-btn-sm"
                  disabled={!!corrBusy}
                  onClick={() => runCorr("/admin/onetime/cleanup-seed-erp-dupe-invoices", {}, "Clean seed↔ERP invoice twins")}
                  data-testid="corr-dedup-twins-btn">
                  <Warning size={11} weight="bold" /> {corrBusy === "Clean seed↔ERP invoice twins" ? "Cleaning…" : "Clean seed↔ERP invoice twins"}
                </button>
                <button className="jde-btn jde-btn-sm"
                  disabled={!!corrBusy}
                  onClick={() => runCorr("/admin/onetime/purge-phantom-invoices", {}, "Purge phantom invoices")}
                  data-testid="corr-purge-phantom-btn">
                  <Warning size={11} weight="bold" /> {corrBusy === "Purge phantom invoices" ? "Purging…" : "Purge phantom invoices (not in ERP)"}
                </button>
                <button className="jde-btn jde-btn-sm"
                  disabled={!!corrBusy}
                  onClick={() => runCorr("/admin/onetime/reconcile-invoice-po-from-erp", {}, "Reconcile invoice↔PO from ERP")}
                  data-testid="corr-reconcile-po-btn">
                  <Warning size={11} weight="bold" /> {corrBusy === "Reconcile invoice↔PO from ERP" ? "Reconciling…" : "Fix over-shipped POs (Excel misfiled to wrong PO)"}
                </button>
                <button className="jde-btn jde-btn-sm"
                  disabled={repDown}
                  onClick={downloadSeedReport}
                  data-testid="corr-seed-report-btn">
                  <DownloadSimple size={11} weight="bold" /> {repDown ? "Preparing…" : "Seed-missing-lines report (xlsx)"}
                </button>
              </div>
              <p className="muted" style={{ fontSize: 10, margin: "8px 0 0" }}>
                ERP sync blocks new invoice twins and prunes existing ones. <strong>Clean seed↔ERP invoice twins</strong> removes
                stale Excel-seed copies that share an invoice # with the live ERP invoice (even across plants).
                <strong> Purge phantom invoices (not in ERP)</strong> removes sales records whose invoice # does NOT exist in the
                live ERP at all (e.g. Legrand 2800111 vs the real 2800139) — the usual cause of "shipped = 2× ordered". <strong>Fix
                over-shipped POs (Excel misfiled to wrong PO)</strong> drops an Excel invoice line that credits an over-shipped PO
                only when that same invoice's own live-ERP lines (dbo.invoicei/Gsti) do NOT contain that (item, PO) pair — i.e. the
                Excel import filed the line under the wrong customer PO (e.g. inv 2800086 tagged PO …891 but the ERP says …890). Both
                dry-run first, are verified against the live ERP, and back up every removed doc (reversible). The report lists
                shipments the Excel seed dropped (since 1-Dec-2025).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice-number conflicts (Excel mis-key audit) ─────────────────── */}
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Receipt size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>INVOICE-NUMBER CONFLICTS — EXCEL MIS-KEY AUDIT</span>
        </span>
        <button className="jde-btn jde-btn-sm" onClick={loadAudit} disabled={auditBusy} data-testid="dh-audit-refresh">
          <ArrowsClockwise size={11} weight="bold" /> {auditBusy ? "Scanning…" : "Re-scan"}
        </button>
      </div>

      {audit && (
        <div className="jde-panel" style={{ marginTop: 12, padding: 16, display: "flex", alignItems: "center", gap: 12 }} data-testid="dh-audit-summary">
          {audit.likely_errors_count > 0
            ? <Warning size={28} weight="fill" color="#DC2626" />
            : <CheckCircle size={28} weight="fill" color="#16A34A" />}
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {audit.likely_errors_count > 0
                ? `${audit.likely_errors_count} likely mis-key${audit.likely_errors_count === 1 ? "" : "s"} — verify against live ERP (dbo.gst / dbo.invoice) and correct`
                : "No invoice-number mis-keys detected"}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>
              Same invoice # stamped on different shipments in one plant + fiscal year. Attribution is split safely by PO+item+sale signature — but the invoice NUMBER on one doc is almost certainly wrong.
            </div>
          </div>
        </div>
      )}

      {audit && audit.likely_errors.map((g) => {
        const key = `err-${g.comp_code}-${g.ms_inv_no}`;
        const isOpen = auditOpen[key] !== false; // errors expanded by default
        return (
          <div key={key} className="jde-panel" style={{ marginTop: 12, border: "1px solid #FCA5A5" }} data-testid={`dh-audit-error-${g.comp_code}-${g.ms_inv_no}`}>
            <div className="jde-panel-header" style={{ cursor: "pointer" }}
              onClick={() => setAuditOpen({ ...auditOpen, [key]: !isOpen })} data-testid={`dh-audit-toggle-${g.comp_code}-${g.ms_inv_no}`}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                <span className="jde-badge" style={{ background: SEV.high.bg, color: SEV.high.fg, fontWeight: 700, fontSize: 9 }}>VERIFY</span>
                <strong>Plant {g.comp_code} · Invoice #{g.ms_inv_no}</strong>
              </span>
              <span className="jde-badge" style={{ background: SEV.high.bg, color: SEV.high.fg, fontWeight: 700 }}>
                {g.distinct_sale_nos} shipments
              </span>
            </div>
            {isOpen && (
              <div className="jde-panel-body" style={{ overflowX: "auto" }}>
                <table className="jde-table" style={{ fontSize: 12, width: "100%" }} data-testid={`dh-audit-table-${g.comp_code}-${g.ms_inv_no}`}>
                  <thead>
                    <tr>{["source", "ms_sale_no", "customer", "po", "pcode", "item", "qty", "sale_date"].map((k) => (
                      <th key={k} style={{ textAlign: "left", textTransform: "uppercase", fontSize: 10 }}>{k.replace(/_/g, " ")}</th>
                    ))}
                    {isSuper && <th style={{ textAlign: "right", textTransform: "uppercase", fontSize: 10 }}>fix</th>}</tr>
                  </thead>
                  <tbody>
                    {g.docs.map((d, i) => (
                      <Fragment key={i}>
                        <tr>
                          <td><span className="jde-badge" style={{ background: d.source === "erp" ? "#DCFCE7" : "#FEF3C7", color: d.source === "erp" ? "#166534" : "#92400E", fontSize: 9, fontWeight: 700 }}>{(d.source || "—").toUpperCase()}</span></td>
                          <td className="text-mono">{fmt(d.ms_sale_no)}</td>
                          <td>{fmt(d.customer)}</td>
                          <td className="text-mono">{fmt(d.po)}</td>
                          <td>{fmt(d.pcode)}</td>
                          <td className="text-mono">{fmt(d.item)}</td>
                          <td className="text-mono">{fmt(d.qty)}</td>
                          <td className="text-mono">{fmt(d.sale_date)}</td>
                          {isSuper && (
                            <td style={{ textAlign: "right" }}>
                              <button className="jde-btn jde-btn-sm" onClick={() => startFix(d.id, g.comp_code, g.ms_inv_no)}
                                disabled={!d.id} data-testid={`dh-fix-btn-${d.id}`} title="Correct this invoice number">
                                <PencilSimple size={11} weight="bold" /> Fix #
                              </button>
                            </td>
                          )}
                        </tr>
                        {isSuper && fix && fix.docId === d.id && (
                          <tr key={`${i}-fix`} data-testid={`dh-fix-row-${d.id}`}>
                            <td colSpan={9} style={{ background: "#F8FAFC", padding: 12 }}>
                              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12 }}>
                                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                                  True invoice #
                                  <input className="jde-input" style={{ marginTop: 4, width: 140 }} value={fix.inv} autoFocus
                                    onChange={(e) => setFix({ ...fix, inv: e.target.value })}
                                    data-testid="dh-fix-inv-input" placeholder="e.g. 1409" />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                                  Plant (opt)
                                  <input className="jde-input" style={{ marginTop: 4, width: 80 }} value={fix.comp}
                                    onChange={(e) => setFix({ ...fix, comp: e.target.value })}
                                    data-testid="dh-fix-comp-input" placeholder="P2" />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569" }}>
                                  ERP Sale_Noc (opt)
                                  <input className="jde-input" style={{ marginTop: 4, width: 120 }} value={fix.saleNoc}
                                    onChange={(e) => setFix({ ...fix, saleNoc: e.target.value })}
                                    data-testid="dh-fix-salenoc-input" placeholder="1409" />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#475569", flex: 1, minWidth: 180 }}>
                                  Reason (opt)
                                  <input className="jde-input" style={{ marginTop: 4 }} value={fix.reason}
                                    onChange={(e) => setFix({ ...fix, reason: e.target.value })}
                                    data-testid="dh-fix-reason-input" placeholder="Verified in dbo.gst" />
                                </label>
                                <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={submitFix} disabled={fix.busy}
                                  data-testid="dh-fix-save">
                                  <Check size={11} weight="bold" /> {fix.busy ? "Saving…" : "Save"}
                                </button>
                                <button className="jde-btn jde-btn-sm" onClick={() => setFix(null)} disabled={fix.busy}
                                  data-testid="dh-fix-cancel">
                                  <X size={11} weight="bold" /> Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {audit && [
        { id: "twins", title: "ERP / Excel twins", list: audit.likely_erp_excel_twins, count: audit.likely_erp_excel_twins_count, note: "Same invoice # + same item across the ERP and Excel feeds — usually the same physical invoice arriving from two sources. De-duped on read; normally safe." },
        { id: "cross", title: "Cross-session reuse", list: audit.cross_session_reuse, count: audit.cross_session_reuse_count, note: "Same invoice # reused in a different fiscal year — expected under an Apr–Mar restart of numbering. Not an error." },
      ].map((sec) => {
        const isOpen = auditOpen[sec.id];
        return (
          <div key={sec.id} className="jde-panel" style={{ marginTop: 12 }} data-testid={`dh-audit-${sec.id}`}>
            <div className="jde-panel-header" style={{ cursor: "pointer" }}
              onClick={() => setAuditOpen({ ...auditOpen, [sec.id]: !isOpen })} data-testid={`dh-audit-toggle-${sec.id}`}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {sec.count > 0 ? (isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />) : <span style={{ width: 12 }} />}
                <span className="jde-badge" style={{ background: SEV.low.bg, color: SEV.low.fg, fontWeight: 700, fontSize: 9 }}>INFO</span>
                <strong>{sec.title}</strong>
              </span>
              <span className="jde-badge" style={{ background: sec.count ? SEV.low.bg : "#DCFCE7", color: sec.count ? SEV.low.fg : "#166534", fontWeight: 700 }}>
                {sec.count}
              </span>
            </div>
            <div className="jde-panel-body">
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>{sec.note}</p>
              {isOpen && sec.list.length > 0 && (
                <div style={{ overflowX: "auto", marginTop: 12 }}>
                  <table className="jde-table" style={{ fontSize: 12, width: "100%" }} data-testid={`dh-audit-table-${sec.id}`}>
                    <thead>
                      <tr>{["plant", "invoice #", "customer", "shipments", "items"].map((k) => (
                        <th key={k} style={{ textAlign: "left", textTransform: "uppercase", fontSize: 10 }}>{k}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {sec.list.map((g, i) => (
                        <tr key={i}>
                          <td>{fmt(g.comp_code)}</td>
                          <td className="text-mono">{fmt(g.ms_inv_no)}</td>
                          <td>{fmt(g.docs[0]?.customer)}</td>
                          <td className="text-mono">{g.distinct_sale_nos}</td>
                          <td className="text-mono">{[...new Set(g.docs.map((d) => d.item).filter(Boolean))].join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Transfer-Pricing EndPcode audit (for the DBA) ──────────────────── */}
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ArrowsLeftRight size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>TRANSFER-PRICING ENDPCODE AUDIT — Pcode · TP code · EndPcode · Voucher# · Invoice#</span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
            FROM
            <input type="date" className="jde-input" style={{ padding: "2px 6px", fontSize: 12 }} value={tpSince}
              onChange={(e) => setTpSince(e.target.value)} data-testid="tp-since-input" />
          </label>
          <label style={{ fontSize: 10, fontWeight: 700, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
            TO
            <input type="date" className="jde-input" style={{ padding: "2px 6px", fontSize: 12 }} value={tpUntil}
              onChange={(e) => setTpUntil(e.target.value)} data-testid="tp-until-input" />
          </label>
          <button className="jde-btn jde-btn-sm" onClick={loadTp} disabled={tpBusy} data-testid="tp-apply-btn">
            <ArrowsClockwise size={11} weight="bold" /> {tpBusy ? "Scanning…" : "Apply"}
          </button>
          <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={downloadTpCsv} disabled={tpDown || !tp?.total} data-testid="tp-download-csv">
            <DownloadSimple size={11} weight="bold" /> {tpDown ? "Preparing…" : "Download CSV"}
          </button>
        </span>
      </div>

      {/* ── Legrand ERP PO audit (raw po_dispatch CustPO vs CustPO2) — production only ── */}
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ArrowsLeftRight size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>
            LEGRAND ERP PO AUDIT — raw CustPO (PoNo) vs CustPO2 per invoice line, resolved SAP PO + mis-entry flag
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: "#94A3B8" }}>reads the live ERP portal · run on production</span>
          <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={downloadLegrandAudit} disabled={lgDown} data-testid="legrand-audit-download">
            <DownloadSimple size={11} weight="bold" /> {lgDown ? "Preparing…" : "Download Excel"}
          </button>
        </span>
      </div>

      {/* ── Superseded-voucher invoices — DBA voids the old invoice# ── */}
      <div className="jde-panel" style={{ marginTop: 20 }} data-testid="superseded-voucher-panel">
        <div className="jde-panel-header" style={{ cursor: "pointer" }} onClick={toggleSv} data-testid="superseded-voucher-toggle">
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {svOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
            <Warning size={14} weight="bold" />
            <strong>SUPERSEDED-INVOICE VOUCHERS — for the DBA to void the old invoice #</strong>
            {svRows && (
              <span className="jde-badge" style={{ background: SEV.medium.bg, color: SEV.medium.fg, fontWeight: 700, fontSize: 9 }}>
                {svRows.total} voucher{svRows.total === 1 ? "" : "s"}
                {svRows.header_still_superseded ? ` · ${svRows.header_still_superseded} header still old` : ""}
              </span>
            )}
          </span>
          <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); downloadSv(); }} disabled={svDown} data-testid="superseded-voucher-download">
            <DownloadSimple size={11} weight="bold" /> {svDown ? "Preparing…" : "Download CSV"}
          </button>
        </div>
        {svOpen && (
          <div className="jde-panel-body">
            <p className="muted" style={{ fontSize: 11, marginTop: 0 }}>
              A dispatch voucher whose export invoice was raised, then re-issued under a NEW number.
              The old (non-GST) number lingers on <span className="text-mono">dbo.po_dispatch.Inv_No</span> /
              <span className="text-mono"> dbo.invoice</span>. Void the <b style={{ color: "#B91C1C" }}>superseded</b> invoice and
              repoint the voucher to the <b style={{ color: "#15803D" }}>final</b> GST-raised one. Read-only — the ERP is not modified here.
            </p>
            {svLoading ? <div className="muted" style={{ fontSize: 12 }}>Reading live ERP…</div>
              : !svRows || !svRows.rows?.length
                ? <div className="muted" style={{ fontSize: 12 }} data-testid="superseded-voucher-empty">No superseded-invoice vouchers found.</div>
                : (
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }} data-testid="superseded-voucher-table">
                    <thead>
                      <tr style={{ background: "#F1F5F9", textAlign: "left" }}>
                        {["Voucher #", "Customer", "Voucher Date", "Header Inv #", "Superseded — VOID", "Final — KEEP", "GST Inv #"].map((h) => (
                          <th key={h} style={{ padding: "6px 9px", border: "1px solid #E2E8F0", whiteSpace: "nowrap", fontSize: 11 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {svRows.rows.map((r) => (
                        <tr key={r.voc_no} data-testid={`superseded-voucher-row-${r.voc_no}`}>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", fontWeight: 700 }} className="text-mono">{r.voc_no}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }}>{r.customer}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", whiteSpace: "nowrap" }} className="text-mono">{r.voucher_date || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }} className="text-mono">
                            {r.header_inv_no || "—"}
                            {r.header_points_to_superseded && (
                              <span title="Voucher header STILL points to the superseded invoice — repoint it to the final one"
                                style={{ marginLeft: 5, fontSize: 9, color: "#B91C1C", border: "1px solid #FCA5A5", borderRadius: 4, padding: "0 3px" }}>
                                old
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", color: "#B91C1C", fontWeight: 700 }} className="text-mono">{(r.superseded_inv_nos || []).join(", ") || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", color: "#15803D", fontWeight: 700 }} className="text-mono">{(r.final_inv_nos || []).join(", ") || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }} className="text-mono">{(r.final_gst_inv_nos || []).join(", ") || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
          </div>
        )}
      </div>

      {/* ── Bad-LineUID watchdog — gsti rows carrying the ERP placeholder 'LineUId' ── */}
      <div className="jde-panel" style={{ marginTop: 20 }} data-testid="bad-lineuid-panel">
        <div className="jde-panel-header" style={{ cursor: "pointer" }} onClick={toggleLu} data-testid="bad-lineuid-toggle">
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {luOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
            <Warning size={14} weight="bold" />
            <strong>BAD LineUID WATCHDOG — ERP invoice lines with an unsubstituted &lsquo;LineUId&rsquo; placeholder</strong>
            {luRows && (
              <span className="jde-badge" style={{ background: SEV.medium.bg, color: SEV.medium.fg, fontWeight: 700, fontSize: 9 }}>
                {luRows.total} line{luRows.total === 1 ? "" : "s"}
              </span>
            )}
          </span>
          <button className="jde-btn jde-btn-sm" onClick={(e) => { e.stopPropagation(); downloadLu(); }} disabled={luDown} data-testid="bad-lineuid-download">
            <DownloadSimple size={11} weight="bold" /> {luDown ? "Preparing…" : "Download CSV"}
          </button>
        </div>
        {luOpen && (
          <div className="jde-panel-body">
            <p className="muted" style={{ fontSize: 11, marginTop: 0 }}>
              Live-ERP <span className="text-mono">dbo.gsti</span> invoice lines whose <span className="text-mono">LineUID</span> is
              the literal template text <b style={{ color: "#B91C1C" }}>&lsquo;LineUId&rsquo;</b> (a data-entry placeholder that was
              never replaced with the real key). These break the per-release Forward Dispatch Sync and should be corrected
              upstream in the ERP. Read-only — the ERP is not modified here.
            </p>
            {luLoading ? <div className="muted" style={{ fontSize: 12 }}>Reading live ERP…</div>
              : !luRows || !luRows.rows?.length
                ? <div className="muted" style={{ fontSize: 12 }} data-testid="bad-lineuid-empty">No bad-LineUID rows found. All clear.</div>
                : (
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }} data-testid="bad-lineuid-table">
                    <thead>
                      <tr style={{ background: "#F1F5F9", textAlign: "left" }}>
                        {["Invoice #", "Customer", "Sale_No", "Sno", "Bad LineUID", "PO No", "Item", "Qty", "Sale Date", "Type"].map((h) => (
                          <th key={h} style={{ padding: "6px 9px", border: "1px solid #E2E8F0", whiteSpace: "nowrap", fontSize: 11 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {luRows.rows.slice(0, 300).map((r, i) => (
                        <tr key={`${r.sale_no}-${r.sno}-${i}`} data-testid={`bad-lineuid-row-${r.sale_no}-${r.sno}`}>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", fontWeight: 700 }} className="text-mono">{r.inv_no || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }}>{r.customer || r.pcode || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }} className="text-mono">{r.sale_no}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }} className="text-mono">{r.sno}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", color: "#B91C1C", fontWeight: 700 }} className="text-mono">{r.bad_lineuid}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }} className="text-mono">{r.po_no || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }} className="text-mono">{r.icode || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", textAlign: "right" }} className="text-mono">{(r.qty || 0).toLocaleString()}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0", whiteSpace: "nowrap" }} className="text-mono">{r.sale_date || "—"}</td>
                          <td style={{ padding: "6px 9px", border: "1px solid #E2E8F0" }}>{r.sale_type || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            {luRows && luRows.rows?.length > 300 && (
              <p className="muted" style={{ fontSize: 11 }}>Showing first 300 of {luRows.total}. Download the CSV for the full list.</p>
            )}
          </div>
        )}
      </div>

      {/* ── Over-shipped POs — full CSV (on-screen check caps at 100) ── */}
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Warning size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>
            OVER-SHIPPED POs — full list (shipped &gt; ordered): CustPO · PoNo · Customer · Ordered · Shipped · Excess
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: "#94A3B8" }}>read-only · runs on the connected DB</span>
          <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={downloadOvershipped} disabled={osDown} data-testid="overshipped-download">
            <DownloadSimple size={11} weight="bold" /> {osDown ? "Preparing…" : "Download CSV"}
          </button>
        </span>
      </div>

      {/* On-screen expandable over-ship drilldown (ERP-verified phantom detection) */}
      <div className="jde-panel" style={{ marginTop: 8 }} data-testid="overship-drill-panel">
        <div className="jde-panel-header" style={{ cursor: "pointer" }} onClick={toggleOsList} data-testid="overship-drill-toggle">
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {osListOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
            <strong>Inspect &amp; fix over-shipped POs {osRows ? `(${osRows.length})` : ""}</strong>
          </span>
          <span className="muted" style={{ fontSize: 10 }}>expand a PO to see its invoices · phantom = not in ERP</span>
        </div>
        {osListOpen && (
          <div className="jde-panel-body" style={{ overflowX: "auto" }}>
            {osLoading && <p className="muted" style={{ fontSize: 12 }}>Loading over-shipped POs…</p>}
            {!osLoading && osRows && osRows.length === 0 && <p className="muted" style={{ fontSize: 12 }}>No over-shipped POs. 🎉</p>}
            {!osLoading && osRows && osRows.map((r, i) => {
              const d = osExpand[r.ms_pono];
              const open = !!d;
              return (
                <div key={i} style={{ borderBottom: "1px solid #EEF2F7" }}>
                  <div onClick={() => toggleOsDetail(r.ms_pono)} data-testid={`overship-row-${r.ms_pono}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", cursor: "pointer", fontSize: 12 }}>
                    {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                    <span className="text-mono" style={{ minWidth: 90 }}>{r.ms_pono}</span>
                    <span style={{ flex: 1 }}>{r.customer_name}</span>
                    <span className="muted">ord {r.ordered}</span>
                    <span style={{ color: "#B91C1C", fontWeight: 700 }}>shp {r.shipped}</span>
                    <span className="jde-badge" style={{ background: "#FEE2E2", color: "#B91C1C", fontSize: 9, fontWeight: 700 }}>+{r.excess}</span>
                  </div>
                  {open && (
                    <div style={{ padding: "4px 4px 12px 26px" }}>
                      {d === "loading" && <p className="muted" style={{ fontSize: 11 }}>Checking invoices against the live ERP…</p>}
                      {d === "error" && <p style={{ fontSize: 11, color: "#B91C1C" }}>Could not load detail.</p>}
                      {d && typeof d === "object" && (
                        <table className="jde-table" style={{ fontSize: 11, width: "100%" }} data-testid={`overship-detail-${r.ms_pono}`}>
                          <thead><tr>{["invoice #", "source", "plant", "sale date", "qty", "in ERP?", ""].map((k) => (
                            <th key={k} style={{ textAlign: "left", textTransform: "uppercase", fontSize: 9 }}>{k}</th>
                          ))}</tr></thead>
                          <tbody>
                            {d.invoices.map((inv, j) => (
                              <tr key={j} style={{ background: inv.phantom ? "#FEF2F2" : "transparent" }}>
                                <td className="text-mono">{inv.ms_inv_no}</td>
                                <td>{inv.source || "—"}</td>
                                <td>{inv.comp_code || "—"}</td>
                                <td>{inv.sale_date || "—"}</td>
                                <td>{inv.qty}</td>
                                <td>
                                  {inv.in_erp === true && <span style={{ color: "#166534", fontWeight: 700 }}>✓ real</span>}
                                  {inv.in_erp === false && <span style={{ color: "#B91C1C", fontWeight: 700 }}>✗ phantom</span>}
                                  {inv.in_erp === null && <span className="muted">n/a</span>}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  {isSuper && inv.phantom && (
                                    <button className="jde-btn jde-btn-sm" style={{ background: "#B91C1C", color: "white" }}
                                      onClick={() => removePhantom(r.ms_pono, inv)}
                                      data-testid={`remove-phantom-${inv.ms_inv_no}`}>
                                      Remove phantom
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {d && typeof d === "object" && !d.erp_verified && (
                        <p style={{ fontSize: 10, color: "#B45309", marginTop: 4 }}>⚠ ERP not reachable — phantom status could not be verified.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>


      {/* ── Duplicate-affected items audit (over-counting duplicate invoices) ── */}
      <div className="jde-toolbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, flexWrap: "wrap", gap: 8 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Receipt size={14} weight="bold" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>
            DUPLICATE-AFFECTED ITEMS — per item: ordered · open now vs open (deduped) · over-count + causing invoice#
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: "#94A3B8" }}>read-only · runs on the connected DB</span>
          <button className="jde-btn jde-btn-sm jde-btn-primary" onClick={downloadDupItems} disabled={dupDown} data-testid="dup-items-download">
            <DownloadSimple size={11} weight="bold" /> {dupDown ? "Preparing…" : "Download CSV"}
          </button>
        </span>
      </div>

      {tp && (
        <div className="jde-panel" style={{ marginTop: 12 }} data-testid="tp-audit-panel">
          <div className="jde-panel-header" style={{ cursor: "pointer" }} onClick={() => setTpOpen(!tpOpen)} data-testid="tp-audit-toggle">
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {tpOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
              <strong>{tp.total} invoice{tp.total === 1 ? "" : "s"} · {tpSince} → {tpUntil}</strong>
            </span>
            <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(tp.counts || {}).map(([k, v]) => {
                const m = TP_ISSUE[k];
                return (
                  <span key={k} className="jde-badge" style={{ background: m ? m.bg : "#DCFCE7", color: m ? m.fg : "#166534", fontWeight: 700, fontSize: 9 }}>
                    {(m ? m.label : "OK").toUpperCase()}: {v}
                  </span>
                );
              })}
            </span>
          </div>
          {tpOpen && (
            <div className="jde-panel-body" style={{ overflowX: "auto" }}>
              <p className="muted" style={{ fontSize: 11, marginTop: 0 }}>
                Live from ERP <span className="text-mono">dbo.gst</span> + <span className="text-mono">dbo.party</span>. Pcode = billing party · TP code = <span className="text-mono">party.TP_CODE</span> · EndPcode = end customer. Hand the CSV to the DBA to backfill missing EndPcodes.
              </p>
              <table className="jde-table" style={{ fontSize: 12, width: "100%" }} data-testid="tp-audit-table">
                <thead>
                  <tr>{["invoice #", "voucher #", "plant", "pcode", "tp code", "endpcode", "sale date", "issues"].map((k) => (
                    <th key={k} style={{ textAlign: "left", textTransform: "uppercase", fontSize: 10 }}>{k}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {tp.rows.map((r, i) => (
                    <tr key={i} data-testid={`tp-audit-row-${i}`}>
                      <td className="text-mono">{fmt(r.invoice_no)}</td>
                      <td className="text-mono">{r.voc_no || <span style={{ color: "#B91C1C" }}>—</span>}</td>
                      <td>{fmt(r.comp_code)}</td>
                      <td title={r.pcode_name || ""}>{fmt(r.pcode)}{r.pcode_name ? <span className="muted" style={{ fontSize: 10 }}> · {r.pcode_name}</span> : null}</td>
                      <td className="text-mono">{fmt(r.tp_code)}</td>
                      <td title={r.endpcode_name || ""}>{r.endpcode ? <>{r.endpcode}{r.endpcode_name ? <span className="muted" style={{ fontSize: 10 }}> · {r.endpcode_name}</span> : null}</> : <span style={{ color: "#B91C1C" }}>—</span>}</td>
                      <td className="text-mono">{fmt(r.sale_date)}</td>
                      <td>
                        {(r.issues || []).length === 0
                          ? <span className="jde-badge" style={{ background: "#DCFCE7", color: "#166534", fontSize: 9, fontWeight: 700 }}>OK</span>
                          : r.issues.map((k) => {
                            const m = TP_ISSUE[k] || { bg: "#E5E7EB", fg: "#374151", label: k };
                            return <span key={k} className="jde-badge" style={{ background: m.bg, color: m.fg, fontSize: 9, fontWeight: 700, marginRight: 4 }}>{m.label}</span>;
                          })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tp.truncated && (
                <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>Showing first {tp.rows.length} of {tp.total}. Download the CSV for the complete list.</p>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 24, borderTop: "1px solid #E2E8F0" }} />


      {data && data.checks.map((c) => {
        const sev = SEV[c.severity] || SEV.low;
        const isOpen = open[c.id];
        const cols = COLS[c.id] || (c.samples[0] ? Object.keys(c.samples[0]) : []);
        return (
          <div key={c.id} className="jde-panel" style={{ marginTop: 12 }} data-testid={`dh-check-${c.id}`}>
            <div className="jde-panel-header" style={{ cursor: "pointer" }}
              onClick={() => setOpen({ ...open, [c.id]: !isOpen })} data-testid={`dh-toggle-${c.id}`}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {c.count > 0 ? (isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />) : <span style={{ width: 12 }} />}
                <span className="jde-badge" style={{ background: sev.bg, color: sev.fg, fontWeight: 700, fontSize: 9 }}>{sev.label}</span>
                <strong>{c.title}</strong>
              </span>
              <span className="jde-badge" style={{ background: c.count ? sev.bg : "#DCFCE7", color: c.count ? sev.fg : "#166534", fontWeight: 700 }}>
                {c.count}
              </span>
            </div>
            <div className="jde-panel-body">
              <p className="muted" style={{ fontSize: 12, margin: 0 }}>{c.description}</p>
              {isOpen && c.samples.length > 0 && (
                <div style={{ overflowX: "auto", marginTop: 12 }}>
                  <table className="jde-table" style={{ fontSize: 12, width: "100%" }} data-testid={`dh-table-${c.id}`}>
                    <thead>
                      <tr>{cols.map((k) => <th key={k} style={{ textAlign: "left", textTransform: "uppercase", fontSize: 10 }}>{k.replace(/_/g, " ")}</th>)}</tr>
                    </thead>
                    <tbody>
                      {c.samples.map((s, i) => (
                        <tr key={i}>{cols.map((k) => <td key={k} className={typeof s[k] === "number" ? "text-mono" : ""}>{fmt(s[k])}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                  {c.count > c.samples.length && (
                    <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>Showing first {c.samples.length} of {c.count}.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </Layout>
  );
}
